/**
 * Remote customization: which remote button does what (owner, 2026-09-27:
 * "remote customization for the app … look at what the old Slique (Sparkle)
 * does and implement those options").
 *
 * Sparkle's model, kept: every button has an action for a PRESS and one for a
 * LONG PRESS, chosen from one list of navigation and player actions, with
 * "Not set" and a reset. Two things are ours:
 *
 *  - The arrows and OK are the way around every menu, and navigation has to be
 *    perfect. They can be changed only for WATCHING — full screen, no control
 *    or popup focused — which is where Sparkle's own remapping matters (zap,
 *    seek). Everywhere else they stay the arrows and OK. Back always steps
 *    back (packages/ui backStack): it is how a viewer gets out of anything.
 *  - A default is either BUILT IN — the code that already handles the button
 *    keeps handling it, untouched (Rule #1: nothing that works today changes
 *    unless the viewer changes it) — or an action this module runs, for the
 *    buttons that used to do nothing at all (Guide, Info, Last channel…).
 */

import { SLEEP_CHOICES } from "../player/sleepTimer.js";

export type RemoteActionId =
  // Navigation
  | "home"
  | "live"
  | "guide"
  | "movies"
  | "series"
  | "mylist"
  | "catchup"
  | "recordings"
  | "search"
  | "settings"
  // Player
  | "channelNext"
  | "channelPrev"
  | "lastChannel"
  | "playPause"
  | "stop"
  | "seekBack"
  | "seekForward"
  | "fullscreen"
  | "showBar"
  | "info"
  | "recents"
  | "playerSettings"
  | "multiview"
  | "pip"
  | "subtitles"
  | "audioTrack"
  | "aspect"
  | "favorite"
  | "mute"
  | "sleepTimer"
  // Nothing: the button is switched off.
  | "none";

export interface RemoteAction {
  id: RemoteActionId;
  label: string;
  group: "navigation" | "player" | "off";
}

export const REMOTE_ACTIONS: readonly RemoteAction[] = [
  { id: "none", label: "Not set", group: "off" },
  { id: "home", label: "Home", group: "navigation" },
  { id: "live", label: "Live TV", group: "navigation" },
  { id: "guide", label: "TV guide", group: "navigation" },
  { id: "movies", label: "Movies", group: "navigation" },
  { id: "series", label: "Series", group: "navigation" },
  { id: "mylist", label: "My List", group: "navigation" },
  { id: "catchup", label: "Catch-up", group: "navigation" },
  { id: "recordings", label: "Recordings", group: "navigation" },
  { id: "search", label: "Search", group: "navigation" },
  { id: "settings", label: "Settings", group: "navigation" },
  { id: "channelNext", label: "Next channel", group: "player" },
  { id: "channelPrev", label: "Previous channel in the list", group: "player" },
  { id: "lastChannel", label: "Last channel watched", group: "player" },
  { id: "playPause", label: "Play / pause", group: "player" },
  { id: "stop", label: "Stop (close the player)", group: "player" },
  { id: "seekBack", label: "Back 10 seconds", group: "player" },
  { id: "seekForward", label: "Forward 30 seconds", group: "player" },
  { id: "fullscreen", label: "Full screen on / off", group: "player" },
  { id: "showBar", label: "Show the player controls", group: "player" },
  { id: "info", label: "Programme info", group: "player" },
  { id: "recents", label: "Recent channels", group: "player" },
  { id: "playerSettings", label: "Player settings", group: "player" },
  { id: "multiview", label: "Multiview", group: "player" },
  { id: "pip", label: "Picture-in-picture", group: "player" },
  { id: "subtitles", label: "Subtitles on / off", group: "player" },
  { id: "audioTrack", label: "Next audio track", group: "player" },
  { id: "aspect", label: "Next picture size (fit / fill / stretch)", group: "player" },
  { id: "favorite", label: "Add to / remove from favourites", group: "player" },
  { id: "mute", label: "Mute on / off", group: "player" },
  // No button has it by default: a viewer assigns it (Settings → Remote control).
  // The cycle is written from the one list of lengths, so a length added to the timer cannot
  // leave this row (and the Help page, which shows these labels) describing the old ones.
  { id: "sleepTimer", label: `Sleep timer (${SLEEP_CHOICES.join(" → ")} → off)`, group: "player" },
];

const ACTION_LABELS = new Map(REMOTE_ACTIONS.map((action) => [action.id, action.label]));

export function actionLabel(id: RemoteActionId): string {
  return ACTION_LABELS.get(id) ?? id;
}

export type RemoteButtonId =
  | "up"
  | "down"
  | "left"
  | "right"
  | "ok"
  | "menu"
  | "info"
  | "guide"
  | "bookmark"
  | "red"
  | "green"
  | "yellow"
  | "blue"
  | "channelUp"
  | "channelDown"
  | "last"
  | "playPause"
  | "stop"
  | "fastForward"
  | "rewind"
  | "next"
  | "previous"
  | "subtitles"
  | "audio";

/** What a button does when the viewer has not changed it. */
export interface ButtonDefault {
  action: RemoteActionId;
  /**
   * The code that already handles this button keeps doing so, and this module
   * stays out of the way. Only a viewer's own choice replaces it.
   */
  builtIn: boolean;
}

export interface RemoteButton {
  id: RemoteButtonId;
  label: string;
  /** `KeyboardEvent.key` values, as the projector's WebView reports them (2026-09-27). */
  keys: readonly string[];
  /** Arrows and OK: changeable only while watching full screen. */
  watchingOnly: boolean;
  /** What it does in the menus and on the pages, in words: never changed here. */
  inMenus: string;
  press: ButtonDefault;
  longPress: ButtonDefault | null;
  /** The default press in words, where one action's name would not say it all. */
  pressText?: string;
}

const builtIn = (action: RemoteActionId): ButtonDefault => ({ action, builtIn: true });
const runs = (action: RemoteActionId): ButtonDefault => ({ action, builtIn: false });

export const REMOTE_BUTTONS: readonly RemoteButton[] = [
  { id: "up", label: "Up", keys: ["ArrowUp"], watchingOnly: true, inMenus: "Moves up", press: builtIn("channelPrev"), longPress: null },
  { id: "down", label: "Down", keys: ["ArrowDown"], watchingOnly: true, inMenus: "Moves down", press: builtIn("channelNext"), longPress: null },
  // Rewind and fast-forward while watching full screen, live TV (its timeshift
  // buffer) and films alike; held, they keep going (owner, 2026-10-10). Last
  // channel and Recent channels stay on the bar and can be put back here in
  // Settings → Remote control ("Last channel" / "Recent channels", which
  // PlayerSurface runs for a built-in Left/Right).
  {
    id: "left", label: "Left", keys: ["ArrowLeft"], watchingOnly: true, inMenus: "Moves left", press: runs("seekBack"), longPress: null,
    pressText: "Rewind 10 seconds",
  },
  {
    id: "right", label: "Right", keys: ["ArrowRight"], watchingOnly: true, inMenus: "Moves right", press: runs("seekForward"), longPress: null,
    pressText: "Forward 30 seconds",
  },
  {
    id: "ok",
    label: "OK",
    keys: ["Enter"],
    watchingOnly: true,
    inMenus: "Selects; hold on a guide programme for its menu",
    press: builtIn("showBar"),
    longPress: null,
  },
  { id: "menu", label: "Menu", keys: ["ContextMenu"], watchingOnly: false, inMenus: "Same as while watching", press: runs("playerSettings"), longPress: null },
  { id: "info", label: "Info", keys: ["Info"], watchingOnly: false, inMenus: "Same as while watching", press: runs("info"), longPress: null },
  { id: "guide", label: "Guide", keys: ["Guide"], watchingOnly: false, inMenus: "Same as while watching", press: runs("guide"), longPress: null },
  { id: "bookmark", label: "Bookmark", keys: ["BrowserFavorites"], watchingOnly: false, inMenus: "Same as while watching", press: runs("favorite"), longPress: null },
  // Colour keys: TV defaults (owner, 2026-10-12). They did nothing before, so nothing that
  // worked changes; a viewer's own choice still wins.
  { id: "red", label: "Red", keys: ["ColorF0Red"], watchingOnly: false, inMenus: "Same as while watching", press: runs("favorite"), longPress: null },
  { id: "green", label: "Green", keys: ["ColorF1Green"], watchingOnly: false, inMenus: "Same as while watching", press: runs("recents"), longPress: null },
  { id: "yellow", label: "Yellow", keys: ["ColorF2Yellow"], watchingOnly: false, inMenus: "Same as while watching", press: runs("lastChannel"), longPress: null },
  { id: "blue", label: "Blue", keys: ["ColorF3Blue"], watchingOnly: false, inMenus: "Same as while watching", press: runs("multiview"), longPress: null },
  { id: "channelUp", label: "CH +", keys: ["ChannelUp"], watchingOnly: false, inMenus: "On Live TV: next channel", press: builtIn("channelNext"), longPress: null },
  { id: "channelDown", label: "CH −", keys: ["ChannelDown"], watchingOnly: false, inMenus: "On Live TV: previous channel", press: builtIn("channelPrev"), longPress: null },
  { id: "last", label: "Last / Recall", keys: ["MediaLast"], watchingOnly: false, inMenus: "Same as while watching", press: runs("lastChannel"), longPress: null },
  { id: "playPause", label: "Play / Pause", keys: ["MediaPlayPause"], watchingOnly: false, inMenus: "Same as while watching", press: builtIn("playPause"), longPress: null },
  { id: "stop", label: "Stop", keys: ["MediaStop"], watchingOnly: false, inMenus: "Same as while watching", press: builtIn("stop"), longPress: null },
  { id: "fastForward", label: "Fast forward", keys: ["MediaFastForward"], watchingOnly: false, inMenus: "Same as while watching", press: runs("seekForward"), longPress: null },
  { id: "rewind", label: "Rewind", keys: ["MediaRewind"], watchingOnly: false, inMenus: "Same as while watching", press: runs("seekBack"), longPress: null },
  { id: "next", label: "Next", keys: ["MediaTrackNext"], watchingOnly: false, inMenus: "Same as while watching", press: runs("channelNext"), longPress: null },
  { id: "previous", label: "Previous", keys: ["MediaTrackPrevious"], watchingOnly: false, inMenus: "Same as while watching", press: runs("channelPrev"), longPress: null },
  { id: "subtitles", label: "Subtitles (CC)", keys: ["ClosedCaptionToggle"], watchingOnly: false, inMenus: "Same as while watching", press: runs("subtitles"), longPress: null },
  { id: "audio", label: "Audio track", keys: ["MediaAudioTrack"], watchingOnly: false, inMenus: "Same as while watching", press: runs("audioTrack"), longPress: null },
];

const BY_KEY = new Map<string, RemoteButton>();
for (const button of REMOTE_BUTTONS) for (const key of button.keys) BY_KEY.set(key, button);

export function buttonForKey(key: string): RemoteButton | undefined {
  return BY_KEY.get(key);
}

export function buttonById(id: RemoteButtonId): RemoteButton | undefined {
  return REMOTE_BUTTONS.find((button) => button.id === id);
}

/** Held this long, a press is a long press (the guide's OK hold uses the same feel). */
export const LONG_PRESS_MS = 600;
